import { useEffect, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Sparkles } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import TagsMenuBar from "@/components/TagsMenuBar";
import { ProtocolCard } from "@/components/ProtocolBuilder";
import { fetchProtocolFocus } from "@/lib/api";
import { useFlyerSelection, FlyerBar } from "@/components/FlyerSelection";

// Protocol Focus (concern) page: title, description, hero image and the
// products in that protocol — all read from Airtable.
export default function ProtocolFocusPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dealer = new URLSearchParams(useLocation().search).get("d") || "orevital";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const flyer = useFlyerSelection(dealer);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchProtocolFocus(slug)
      .then((d) => active && setData(d))
      .catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    window.scrollTo({ top: 0 });
    return () => {
      active = false;
    };
  }, [slug]);

  const openProduct = (p) => navigate(`/d/${dealer}?view=product&p=${encodeURIComponent(p.slug)}`);
  const focus = data?.focus;
  const products = data?.products || [];
  // Hero: Protocol Focus Image_url from Airtable when set, else the protocol's
  // Before & After image (which then also gets its own block when not the hero).
  const heroImage = focus?.hero_image || "";

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f7f4]" data-testid="protocol-focus-page">
      <PublicHeader nextPath={`/protocols/${slug}?d=${dealer}`} />
      <TagsMenuBar activeSlug={focus?.slug || slug} />
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 py-5 sm:py-8 space-y-6">
          <Link to={`/d/${dealer}?view=protocols${focus ? `&focus=${encodeURIComponent(focus.name)}` : ""}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0b2545] hover:underline" data-testid="protocol-focus-back">
            <ArrowLeft size={14} /> Build Your OreVital Protocol
          </Link>

          {loading ? (
            <div className="py-20 text-center text-zinc-500"><Loader2 size={18} className="animate-spin inline mr-2" />Loading protocol…</div>
          ) : !focus ? (
            <div className="py-20 text-center text-zinc-500">This protocol focus isn't available.</div>
          ) : (
            <>
              <section className="rounded-3xl overflow-hidden bg-[#0b2545] text-white grid sm:grid-cols-5" data-testid="protocol-focus-hero">
                {heroImage && (
                  <div className="sm:col-span-2 bg-white/5 min-h-[200px]">
                    <img src={heroImage} alt={focus.name} className="w-full h-full object-cover max-h-72 sm:max-h-none" onError={(e) => (e.currentTarget.style.display = "none")} />
                  </div>
                )}
                <div className={`p-6 sm:p-8 ${heroImage ? "sm:col-span-3" : "sm:col-span-5"}`}>
                  <div className="text-[11px] uppercase tracking-[3px] text-[#d6ad52] font-semibold inline-flex items-center gap-1.5"><Sparkles size={12} /> Protocol Focus</div>
                  <h1 className="mt-1 text-3xl sm:text-4xl font-bold leading-tight" data-testid="protocol-focus-title">{focus.name}</h1>
                  {focus.description && <p className="mt-3 text-sm sm:text-base text-white/85 leading-relaxed max-w-2xl" data-testid="protocol-focus-description">{focus.description}</p>}
                  <p className="mt-4 text-xs text-white/60">{products.length} OreVital product{products.length === 1 ? "" : "s"} in this protocol</p>
                  {products.length > 0 && (
                    <Link to={`/protocols/flyer?d=${dealer}&focus=${encodeURIComponent(focus.name)}&ids=${products.map((p) => p.id).join(",")}`} className="inline-flex items-center gap-1.5 mt-4 h-9 px-4 rounded-full bg-[#d6ad52] text-[#0b2545] text-xs font-bold hover:bg-[#e2bd66]" data-testid="protocol-focus-create-flyer">
                      Create {focus.name} flyer →
                    </Link>
                  )}
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide">Products &amp; peptides for {focus.name}</h2>
                  {products.length > 0 && (
                    <button type="button" onClick={() => products.forEach((p) => { if (!flyer.has(p.id)) flyer.toggle(p.id); })} className="text-xs font-semibold text-violet-700 hover:underline" data-testid="protocol-focus-flyer-all">
                      Add all to flyer
                    </button>
                  )}
                </div>
                {products.length === 0 ? (
                  <div className="py-10 text-center text-zinc-500 text-sm rounded-2xl border border-dashed border-zinc-200">No products are tagged for this focus yet.</div>
                ) : (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="protocol-focus-products">
                    {products.map((p) => (
                      <ProtocolCard key={p.id} product={p} selected={[focus.name]} onOpen={() => openProduct(p)} inFlyer={flyer.has(p.id)} onToggleFlyer={() => flyer.toggle(p.id)} />
                    ))}
                  </ul>
                )}
              </section>

              {focus.before_after_image && focus.before_after_image !== heroImage && (
                <section className="rounded-3xl overflow-hidden border border-zinc-200 bg-white grid sm:grid-cols-5" data-testid="protocol-focus-before-after">
                  <div className="sm:col-span-3 bg-zinc-50">
                    <img src={focus.before_after_image} alt={`${focus.name} before and after`} className="w-full h-full object-cover max-h-80" loading="lazy" onError={(e) => (e.currentTarget.style.display = "none")} />
                  </div>
                  <div className="sm:col-span-2 p-6 flex flex-col justify-center">
                    <div className="text-[10px] uppercase tracking-[2px] text-[#b88a2b] font-bold">Real people, real results</div>
                    <h2 className="text-xl font-bold text-[#0b2545] mt-1">Before &amp; After</h2>
                    <p className="text-sm text-zinc-600 mt-2 leading-relaxed">Customer results for the {focus.name} protocol. Follow the routine consistently and share your own progress with your OreVital dealer.</p>
                  </div>
                </section>
              )}

              {data.all_focus?.length > 1 && (
                <section>
                  <h2 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide mb-2">Explore other protocols</h2>
                  <div className="flex flex-wrap gap-2" data-testid="protocol-focus-others">
                    {data.all_focus.filter((f) => f.slug !== focus.slug).map((f) => (
                      <Link key={f.slug} to={`/protocols/${f.slug}?d=${dealer}`} className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-700 hover:border-violet-300 hover:bg-violet-50 transition-colors" data-testid={`protocol-focus-other-${f.slug}`}>
                        {f.name}
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>
      <FlyerBar dealer={dealer} ids={flyer.ids} onClear={flyer.clear} focus={focus?.name || ""} />
    </div>
  );
}
